import { nowUTC } from "@homebound/activesupport";
import { PageBanner } from "@homebound/beam";
import { useState } from "react";
import { pluralize } from "src/utils";
import {
  ReloadCostsBannerOptionsQuery,
  ReloadCostsBannerQuery,
  useReloadCostsBannerOptionsQuery,
  useReloadCostsBannerQuery,
} from "./ReloadCostsBanner.generated";

export enum ReloadCostsBannerType {
  Configurations = "configurations",
  Options = "options",
}

type ReloadCostsBannerProps = {
  type: ReloadCostsBannerType;
  productOfferingIds: string[];
  onReload: () => Promise<unknown> | void;
};

/** Polls for configurations modified after the costs were loaded, and asks to reload when any exist. */
export function ReloadCostsBanner(props: ReloadCostsBannerProps) {
  const { productOfferingIds, onReload, type } = props;
  const [loadedAt, setLoadedAt] = useState(nowUTC);

  // Since both queries use the same filter and close to the same return, we can use a single hook and switch on the type
  const query = (
    type === ReloadCostsBannerType.Configurations ? useReloadCostsBannerQuery : useReloadCostsBannerOptionsQuery
  )({
    variables: { filter: { productOfferings: productOfferingIds, afterUpdatedAt: loadedAt } },
    // Refresh every 15 seconds
    pollInterval: 15_000,
    fetchPolicy: "no-cache",
    skip: productOfferingIds.isEmpty,
  });
  const updatedEntities =
    (type === ReloadCostsBannerType.Configurations
      ? (query.data as ReloadCostsBannerQuery)?.costConfigurationSummaries.entities.length
      : (query.data as ReloadCostsBannerOptionsQuery)?.costOptionGroupSummaries
          .flatMap((o) => o.offerings)
          .map((o) => o.summary)
          .compact().length) ?? 0;

  if (updatedEntities === 0) {
    return null;
  }

  const noun = type === ReloadCostsBannerType.Configurations ? "configuration" : "option";

  return (
    <PageBanner
      type="info"
      title="Updated Costs Ready"
      description={`Updated calculations are ready for ${updatedEntities} ${pluralize(updatedEntities, noun)}.`}
      primaryAction={{
        label: "Refresh Costs",
        onClick: async () => {
          setLoadedAt(nowUTC());
          await onReload();
        },
      }}
    />
  );
}
